import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { fetchHealthFacilityFromFirebase } from "../../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import {
  fetchAuthorizationLetterConfig,
  updateAuthorizationLetterConfig,
} from "./api/authorizationLetterConfigService";
import AuthorizationLetterConfigForm from "./components/authorizationLetterConfigForm/authorizationLetterConfigForm";
import SaveChangesBar from "./components/saveChangesBar/saveChangesBar";
import UpdateInfo from "./components/updateInfo/updateInfo";

export interface AuthorizationLetterConfig {
  approval_keywords?: string[];
  denial_keywords?: string[];
  enabled?: boolean;
  loinc_code?: string[];
  lookback_days?: number;
  negation_keywords?: string[];
  resource_type_text?: string;
  updated_at?: string;
  updated_by?: string;
}

const AuthorizationLetterConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: healthFacilityData } = useSelector(
    (state: RootState) => state.healthFacility,
  );
  const { user } = useAuthSelector((state) => state.firebaseAuthentication);

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [config, setConfig] = useState<AuthorizationLetterConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchHealthFacilityFromFirebase());
  }, [dispatch]);

  useEffect(() => {
    if (selectedOrgId) {
      loadConfig();
    } else {
      setConfig(null);
      setHasUnsavedChanges(false);
    }
  }, [selectedOrgId]);

  const loadConfig = async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    try {
      const data = await fetchAuthorizationLetterConfig(selectedOrgId);
      setConfig(
        data || {
          approval_keywords: [],
          denial_keywords: [],
          enabled: true,
          loinc_code: [],
          lookback_days: 90,
          negation_keywords: [],
          resource_type_text: "",
        },
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error loading config:", error);
      toast.error("Failed to load authorization letter configuration");
      setConfig({
        approval_keywords: [],
        denial_keywords: [],
        enabled: true,
        loinc_code: [],
        lookback_days: 90,
        negation_keywords: [],
        resource_type_text: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedOrgId || !config) return;

    setSaving(true);
    try {
      await updateAuthorizationLetterConfig(
        selectedOrgId,
        config,
        user?.email || user?.id,
      );
      setHasUnsavedChanges(false);
      toast.success(
        ToastMessages.OPTION_SAVE_ALL_SUCCESS ||
          "Configuration saved successfully",
      );
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save authorization letter configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (selectedOrgId) {
      loadConfig();
    }
  };

  const handleFieldChange = (
    fieldName: keyof AuthorizationLetterConfig,
    value: any,
  ) => {
    if (!config) return;
    setConfig({
      ...config,
      [fieldName]: value,
    });
    setHasUnsavedChanges(true);
  };

  const organizationOptions =
    healthFacilityData?.map((facility) => ({
      label: facility.Name ?? "",
      value: facility.DocId ?? "",
    })) ?? [];

  const handleOrganizationChange = (option: { value: string }) => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to switch organizations?",
      );
      if (!confirmDiscard) return;
    }
    setSelectedOrgId(option.value);
  };

  return (
    <div className="authorization-letter-config flex h-full flex-col gap-4 overflow-hidden">
      {/* Header with Organization Selector */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
        <div className="text-base font-semibold">
          Authorization Letter Configuration
        </div>
        <div className="w-[30%]">
          <Select
            id="organization"
            options={organizationOptions}
            label=""
            placeholder="Select Organization"
            onOptionChange={handleOrganizationChange}
            defaultValue={selectedOrgId}
          />
        </div>
      </div>

      {!selectedOrgId ? (
        <div className="flex h-40 items-center justify-center text-center text-sm text-gray-500">
          Please select an organization to view and manage authorization letter
          configuration.
        </div>
      ) : loading ? (
        <div className="flex-1">
          <LoaderMessage message="Loading authorization letter configuration..." />
        </div>
      ) : config ? (
        <>
          <AuthorizationLetterConfigForm
            config={config}
            onFieldChange={handleFieldChange}
          />

          <SaveChangesBar
            hasUnsavedChanges={hasUnsavedChanges}
            saving={saving}
            onSave={handleSave}
            onDiscard={handleDiscard}
          />

          <UpdateInfo
            updated_at={config.updated_at}
            updated_by={config.updated_by}
          />
        </>
      ) : null}
    </div>
  );
};

export default AuthorizationLetterConfiguration;
